import { Decimal } from 'decimal.js';

export type RecalculableAmount<Key> = {
  key: Key;
  /** Current amount in cents. */
  amount: number;
};

export type RecalculatedAmount<Key> = RecalculableAmount<Key> & { nextAmount: number };

/**
 * Re-spread amounts over a new total, keeping the share each line represents. Used for a campaign's
 * allocations against a changed operation amount, and for an employee's choices against their
 * changed allocation. Rounding remainders land on the largest line.
 */
export function recalculateAmounts<Key>(
  entries: RecalculableAmount<Key>[],
  nextTotal: number,
): RecalculatedAmount<Key>[] {
  const currentTotal = entries.reduce((sum, entry) => sum + entry.amount, 0);

  if (currentTotal === 0 || currentTotal === nextTotal) {
    return [];
  }

  const recalculated = entries.map((entry) => ({
    ...entry,
    nextAmount: new Decimal(entry.amount).mul(nextTotal).div(currentTotal).round().toNumber(),
  }));

  const remainder = nextTotal - recalculated.reduce((sum, entry) => sum + entry.nextAmount, 0);
  const largestIndex = recalculated.reduce(
    (best, entry, index) =>
      entry.nextAmount > (recalculated[best]?.nextAmount ?? 0) ? index : best,
    0,
  );

  return recalculated.map((entry, index) =>
    index === largestIndex ? { ...entry, nextAmount: entry.nextAmount + remainder } : entry,
  );
}
